import { DestroyRef, inject, Injectable, InjectionToken } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/** Le nom du canal que partagent les onglets de CDT d'un même navigateur. */
const CHANNEL_NAME = 'cdt.parcours';

type Channel = Pick<BroadcastChannel, 'postMessage' | 'close'> & {
  onmessage: ((event: MessageEvent) => void) | null;
};

/** Ouvre le canal entre onglets ; `null` quand le navigateur n'en a pas. */
export const TOUR_COMPLETION_CHANNEL = new InjectionToken<((name: string) => Channel) | null>(
  'TOUR_COMPLETION_CHANNEL',
  {
    providedIn: 'root',
    factory: () =>
      typeof BroadcastChannel === 'undefined' ? null : (name) => new BroadcastChannel(name),
  },
);

interface TourCompletedMessage {
  type: 'tour-completed';
  tourId: string;
}

function isTourCompleted(data: unknown): data is TourCompletedMessage {
  const message = data as Partial<TourCompletedMessage> | null;
  return message?.type === 'tour-completed' && typeof message.tourId === 'string';
}

/**
 * Le parcours suivi jusqu'au bout, d'un onglet à l'autre. L'onglet de démonstration n'appelle
 * jamais l'API : il signale le parcours suivi, et c'est l'onglet de travail qui l'enregistre
 * (RG-nouveaute-memoire). Sans onglet de travail ouvert, personne ne l'entend, et rien n'est retenu.
 */
@Injectable({ providedIn: 'root' })
export class TourCompletion {
  private readonly completed = new Subject<string>();
  private readonly channel = inject(TOUR_COMPLETION_CHANNEL)?.(CHANNEL_NAME) ?? null;

  /** Les parcours suivis jusqu'au bout dans un autre onglet. */
  readonly completed$: Observable<string> = this.completed.asObservable();

  constructor() {
    if (this.channel) {
      this.channel.onmessage = (event) => {
        if (isTourCompleted(event.data)) this.completed.next(event.data.tourId);
      };
    }
    inject(DestroyRef).onDestroy(() => this.channel?.close());
  }

  signal(tourId: string): void {
    const message: TourCompletedMessage = { type: 'tour-completed', tourId };
    this.channel?.postMessage(message);
  }
}
